// The Brain asking before it goes on (plan brain-conversacion-en-segundo-plano,
// D7): its questions, and room to answer something else -- the options are the
// Brain's guess at what fits, not a cage.
//
// **One option per line**, not buttons in a row: an option can be a whole
// sentence, and a row of long buttons wraps into a mess. **Choosing does not
// send**: you pick, you can change your mind, and only «Send» answers. Several
// questions go in tabs, and nothing is sent until the last one is answered --
// half an answer would leave the Brain guessing the rest.
//
// Inside the thread and not a dialog, for the same reason as the confirmation
// card: what was asked and what was answered is part of the conversation.
// Answering is a message like any other; the run goes on from it.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Circle, CircleDot, HelpCircle, Square, SquareCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CalloutCard } from '@/components/ui/callout-card'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { answerText, type PersonQuestion } from '@/services/brainApi'

export function QuestionCard({
  questions,
  onAnswer,
  busy,
}: {
  questions: PersonQuestion[]
  onAnswer: (answer: string) => void
  busy?: boolean
}) {
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)
  const [chosen, setChosen] = useState<string[][]>(() => questions.map(() => []))
  const [other, setOther] = useState<string[]>(() => questions.map(() => ''))

  const answers = questions.map((_, i) => [...chosen[i], ...(other[i].trim() ? [other[i].trim()] : [])])
  const answered = answers.map((a) => a.length > 0)
  const last = tab === questions.length - 1
  const question = questions[tab]

  const choose = (option: string) => {
    setChosen((all) =>
      all.map((picked, i) => {
        if (i !== tab) return picked
        if (!question.multiple) return [option]
        return picked.includes(option) ? picked.filter((o) => o !== option) : [...picked, option]
      }),
    )
    // One choice: an option and something written are two answers to pick from.
    if (!question.multiple) setOther((all) => all.map((text, i) => (i === tab ? '' : text)))
  }

  const write = (text: string) => {
    setOther((all) => all.map((old, i) => (i === tab ? text : old)))
    if (!question.multiple && text.trim()) setChosen((all) => all.map((picked, i) => (i === tab ? [] : picked)))
  }

  const [On, Off] = question.multiple ? [SquareCheck, Square] : [CircleDot, Circle]

  return (
    <CalloutCard icon={HelpCircle} className="mt-3">
      {questions.length > 1 && (
        <Tabs value={String(tab)} onValueChange={(value) => setTab(Number(value))}>
          <TabsList className="mb-3 h-8">
            {questions.map((q, i) => (
              <TabsTrigger key={q.text} value={String(i)} className="h-6 gap-1 px-2 text-xs">
                {t('brain.pregunta_n', { n: i + 1 })}
                {answered[i] && <Check className="h-3 w-3" aria-label={t('brain.pregunta_contestada')} />}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      <p className="text-sm font-medium">{question.text}</p>
      {question.multiple && <p className="mt-1 text-xs text-muted-foreground">{t('brain.pregunta_varias')}</p>}

      {question.options.length > 0 && (
        <div role={question.multiple ? 'group' : 'radiogroup'} aria-label={question.text} className="mt-3 space-y-1">
          {question.options.map((option) => {
            const on = chosen[tab].includes(option)
            const Icon = on ? On : Off
            return (
              <Button
                key={option}
                type="button"
                variant="ghost"
                size="fila"
                aria-label={option}
                role={question.multiple ? 'checkbox' : 'radio'}
                aria-checked={on}
                disabled={busy}
                onClick={() => choose(option)}
                className={cn(on && 'bg-muted font-medium')}
              >
                <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', on ? 'text-primary' : 'text-muted-foreground')} />
                <span>{option}</span>
              </Button>
            )
          })}
        </div>
      )}

      <Input
        value={other[tab]}
        onChange={(event) => write(event.target.value)}
        placeholder={t('brain.pregunta_otra')}
        aria-label={t('brain.pregunta_otra')}
        disabled={busy}
        className="mt-3 h-9"
      />

      <div className="mt-3 flex justify-end">
        {last ? (
          <Button
            size="sm"
            disabled={busy || !answered.every(Boolean)}
            onClick={() => onAnswer(answerText(questions, answers))}
          >
            {t('brain.pregunta_enviar')}
          </Button>
        ) : (
          <Button size="sm" variant="secondary" disabled={!answered[tab]} onClick={() => setTab(tab + 1)}>
            {t('brain.pregunta_siguiente')}
          </Button>
        )}
      </div>
    </CalloutCard>
  )
}
